import {
  View,
  StyleSheet,
  Text,
  Pressable,
  ActivityIndicator,
} from "react-native";
import { ms } from "@/ui/moderateScale";
import Heading from "@/ui/Heading";
import { useTranslation } from "react-i18next";
import { useUserSlice } from "@/models/useUserSlice";
import { Colors } from "@/constants/Colors";
import { downloadFile, fileExists, formatDuration } from "@/models/utils";
import Icon from "@/ui/Icon";
import { PlayerProps, useAudioPlayer } from "@/models/useAudioPlayer";
import React, { useEffect, useRef } from "react";
import { Note } from "@/models/types";

export default function NoteTabTranscript({ note }: { note: Note }) {
  const { t } = useTranslation();
  const [audioUrl, setAudioUrl] = React.useState<string | null>(null);
  const urlParams = new URLSearchParams(window.location.search);
  const [searchResult, setSearchResult] = React.useState<{
    start: number;
    highlight: string;
  } | null>(null);
  const highlightedSegmentRef = useRef<View>(null);

  const isMediaNote = ["voice_recording", "media_import"].includes(note?.type);
  const [loading, setLoading] = React.useState(isMediaNote);

  const player = useAudioPlayer(audioUrl);

  useEffect(() => {
    if (loading) {
      if (isMediaNote) {
        fileExists(note.fileUrl).then((exists) => {
          if (exists) {
            setLoading(false);
            setAudioUrl(note.fileUrl);
          }
        });
      }
    } else {
      const searchSegmentStart = urlParams.get("segmentStart");
      const searchSegmentText = urlParams.get("segmentText");
      if (searchSegmentStart && searchSegmentText) {
        setSearchResult({
          start: Number(searchSegmentStart),
          highlight: decodeURIComponent(searchSegmentText),
        });

        // Schedule scrolling after the component has fully rendered
        setTimeout(() => {
          if (highlightedSegmentRef.current) {
            // @ts-ignore - scrollIntoView is available in web environments
            highlightedSegmentRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 500);
      }
    }
    return () => {
      player.pause();
      player.seekTo(0);
    };
  }, [note, loading]);

  const onEditSegment = (segment: Note["transcript"][number]) => {
    useUserSlice.getState().note().editTranscript(note.id, segment);
  };

  const onRenameSpeaker = (segment: Note["transcript"][number]) => {
    useUserSlice.getState().note().renameSpeaker(note.id, segment);
  };

  const renderHighlightedText = (text: string) => {
    return (
      <Text style={styles.transcriptText}>
        {(() => {
          const parts = text.split(/<mark>(.*?)<\/mark>/);

          if (parts.length > 1) {
            return parts.map((part, i) => {
              if (i % 2 === 1) {
                // This is the highlighted part (inside <mark> tags)
                return (
                  <Text key={i} style={styles.highlight}>
                    {part}
                  </Text>
                );
              } else {
                // This is regular text (outside <mark> tags)
                return part ? <Text key={i}>{part}</Text> : null;
              }
            });
          }
          return text;
        })()}
      </Text>
    );
  };

  if (!note) return null;

  if (loading) {
    return (
      <View
        style={{
          flex: 1,
          minHeight: ms(600),
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <ActivityIndicator size="small" color={Colors.text} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {audioUrl && <NoteTabTranscript.Player player={player} note={note} />}

      <View style={styles.transcriptContainer}>
        {note.recordingNotes && (
          <View style={{ marginBottom: ms(24) }}>
            <Heading
              text={t("noteTabTranscript.recordingNotes")}
              style={styles.transcriptHeading}
            />
            <Text style={styles.transcriptText}>{note.recordingNotes}</Text>
          </View>
        )}

        <Heading
          text={t("noteTabTranscript.transcript")}
          style={styles.transcriptHeading}
        />
        {note.transcript.map((segment, index) => {
          const start = segment.start || 0;
          const end = segment.end || player.duration;
          const isSelected = player?.position > start && player?.position < end;
          const isHighlighted = segment?.start === searchResult?.start;
          const previousSegment = note.transcript[index - 1];
          const isSameSpeaker = previousSegment?.speaker === segment.speaker;

          return (
            <View
              key={segment.start}
              style={styles.transcriptItem}
              ref={isHighlighted ? highlightedSegmentRef : null}
            >
              <NoteTabTranscript.SegmentHeader
                audioUrl={audioUrl}
                isSelected={isSelected}
                player={player}
                isSameSpeaker={isSameSpeaker}
                start={start}
                segment={segment}
                onEditSegment={() => onEditSegment(segment)}
                onRenameSpeaker={() => onRenameSpeaker(segment)}
              />
              <Pressable
                onPress={() => {
                  player.seekTo(start);
                  if (!player.isPlaying) {
                    player.play();
                  }
                }}
                style={[
                  styles.segmentPressable,
                  isSelected && styles.selectedSegment,
                ]}
              >
                {isHighlighted ? (
                  renderHighlightedText(searchResult.highlight)
                ) : (
                  <Text style={styles.transcriptText}>{segment.text}</Text>
                )}
              </Pressable>
            </View>
          );
        })}
      </View>
    </View>
  );
}

NoteTabTranscript.Player = ({
  player,
  note,
}: {
  player: PlayerProps;
  note: Note;
}) => {
  const { t } = useTranslation();
  const onRateChange = (rate: number, setRate: (rate: number) => void) => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
    const currentIndex = rates.indexOf(rate);
    const nextIndex = (currentIndex + 1) % rates.length;
    setRate(rates[nextIndex]);
  };
  const downloadAudio = async () => {
    downloadFile({
      type: "url",
      url: note.fileUrl,
      fileName: `${note.name}${note.fileUrl.substring(note.fileUrl.lastIndexOf("."))}`,
    });
  };
  const getExpirationDays = () => {
    const now = new Date();
    const expirationDate = new Date(note.createdAt);
    expirationDate.setDate(expirationDate.getDate() + 14);
    return Math.ceil(
      (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );
  };
  const duration = note.transcript[note.transcript.length - 1].end;
  return (
    <>
      <View style={styles.playerContainer}>
        <Pressable
          style={styles.playButton}
          onPress={player.isPlaying ? player.pause : player.play}
        >
          <Icon
            icon={player.isPlaying ? "pause" : "play"}
            style={styles.playIcon}
          />
        </Pressable>

        <Pressable
          style={styles.rateButton}
          onPress={() => onRateChange(player.rate, player.setRate)}
        >
          <Text style={styles.rateText}>{player.rate}X</Text>
        </Pressable>

        <Text style={styles.durationText}>
          {formatDuration(player.position)} / {formatDuration(duration)}
        </Text>

        <View style={styles.progressContainer}>
          <View style={styles.progressTrack}>
            <View
              style={[
                styles.transcriptProgressBar,
                {
                  width: `${Math.min((player.position / player.duration) * 100, 100)}%`,
                },
              ]}
            />
          </View>
        </View>
        <Pressable
          onPress={downloadAudio}
          style={{
            width: ms(48),
            height: ms(48),
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            icon="download"
            style={{ width: ms(18), height: ms(18), tintColor: Colors.text }}
          />
        </Pressable>
      </View>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <View />
        <Text
          style={{ color: Colors.softRed, fontSize: ms(12), fontWeight: "500" }}
        >
          {t("noteTabTranscript.expiresIn", { days: getExpirationDays() })}
        </Text>
      </View>
    </>
  );
};

NoteTabTranscript.SegmentHeader = ({
  audioUrl,
  isSelected,
  player,
  isSameSpeaker,
  start,
  segment,
  onEditSegment,
  onRenameSpeaker,
}: {
  audioUrl: string | null;
  isSelected: boolean;
  player: PlayerProps;
  isSameSpeaker: boolean;
  start: number;
  segment: Note["transcript"][number];
  onEditSegment: () => void;
  onRenameSpeaker: () => void;
}) => {
  const { t } = useTranslation();

  const getSpeakerText = () => {
    const text = isNaN(Number(segment.speaker))
      ? segment.speaker
      : `${t("noteTabTranscript.speaker")} ${Number(segment.speaker) + 1}`;

    const firstLetter = isNaN(Number(segment.speaker))
      ? segment.speaker.charAt(0).toUpperCase()
      : String(Number(segment.speaker) + 1);

    return { text, firstLetter };
  };

  const getColorByLetter = () => {
    const letter = getSpeakerText().firstLetter;

    const colors = {
      A: "#E53935", // Bright Red
      B: "#43A047", // Forest Green
      C: "#1E88E5", // Clear Blue
      D: "#FFB300", // Amber
      E: "#8E24AA", // Purple
      F: "#00ACC1", // Cyan
      G: "#FB8C00", // Orange
      H: "#5E35B1", // Deep Purple
      I: "#3949AB", // Indigo
      J: "#039BE5", // Light Blue
      K: "#D81B60", // Pink
      L: "#7CB342", // Light Green
      M: "#00897B", // Teal
      N: "#546E7A", // Blue Grey
      O: "#6D4C41", // Brown
      P: "#F4511E", // Brick Red
      Q: "#757575", // Grey
      R: "#9E9D24", // Olive
      S: "#FDD835", // Yellow
      T: "#BF360C", // Deep Orange
      U: "#6200EA", // Violet
      V: "#004D40", // Dark Teal
      W: "#2E7D32", // Forest Green Alt
      X: "#0D47A1", // Navy Blue
      Y: "#4A148C", // Dark Purple
      Z: "#1A237E", // Dark Blue
      0: "#212121", // Nearly Black
      1: "#33691E", // Moss Green
      2: "#01579B", // Dark Cyan
      3: "#3E2723", // Dark Brown
      4: "#E65100", // Pumpkin
      5: "#880E4F", // Magenta
      6: "#006064", // Deep Teal
      7: "#B71C1C", // Crimson
      8: "#4A148C", // Royal Purple
      9: "#1B5E20", // Emerald Green
    };

    return colors[letter.toUpperCase()];
  };

  return (
    <View style={styles.timestampContainer}>
      <View style={styles.timestampLeftContainer}>
        {segment.speaker && !isSameSpeaker && (
          <Pressable
            onPress={onRenameSpeaker}
            style={{ flexDirection: "row", alignItems: "center", gap: ms(8) }}
          >
            <View
              style={{
                backgroundColor: getColorByLetter(),
                borderRadius: ms(100),
                width: ms(20),
                height: ms(20),
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  color: Colors.white,
                  fontSize: ms(10),
                  fontWeight: "600",
                }}
              >
                {getSpeakerText().firstLetter}
              </Text>
            </View>
            <Text style={styles.speakerText}>{getSpeakerText().text}</Text>
          </Pressable>
        )}
        {audioUrl && (
          <Pressable
            onPress={() => {
              if (isSelected && player.isPlaying) {
                player.pause();
                return;
              }

              player.seekTo(start);
              if (!player.isPlaying) {
                player.play();
              }
            }}
          >
            <Icon
              icon={isSelected && player.isPlaying ? "pause" : "play"}
              style={styles.playTimestampIcon}
            />
          </Pressable>
        )}
        <Text onPress={onEditSegment} style={styles.timestampText}>
          {formatDuration(Number(segment.start))}
        </Text>
      </View>

      <Pressable style={styles.editButton} onPress={onEditSegment}>
        <Icon icon="pen" style={styles.editIcon} />
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginBottom: ms(32),
  },
  transcriptContainer: {
    marginTop: ms(16),
  },
  transcriptHeading: {
    marginBottom: ms(12),
    fontSize: ms(16),
    color: Colors.accent,
  },
  transcriptItem: {
    marginBottom: ms(16),
  },
  timestampContainer: {
    flexDirection: "row",
    gap: ms(8),
    alignItems: "center",
    justifyContent: "space-between",
    height: ms(24),
  },
  timestampLeftContainer: {
    flexDirection: "row",
    gap: ms(8),
    alignItems: "center",
  },
  playTimestampIcon: {
    width: ms(16),
    height: ms(16),
    tintColor: Colors.subtitle,
    marginLeft: -ms(4),
  },
  timestampText: {
    color: Colors.subtitle,
    textDecorationStyle: "dotted",
    textDecorationLine: "underline",
    lineHeight: ms(24),
    fontSize: ms(11),
  },
  speakerText: {
    color: Colors.text,
    lineHeight: ms(24),
    fontSize: ms(14),
    marginRight: ms(4),
    fontWeight: "600",
    // letterSpacing: ms(1),
  },
  transcriptText: {
    color: Colors.text,
    lineHeight: ms(24),
    fontSize: ms(15),
  },
  playerContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: ms(8),
    marginBottom: ms(8),
    backgroundColor: Colors.text + "10",
    borderRadius: ms(8),
    height: ms(48),
  },
  playButton: {
    width: ms(48),
    height: ms(48),
    alignItems: "center",
    justifyContent: "center",
  },
  playIcon: {
    tintColor: Colors.text,
    width: ms(24),
    height: ms(24),
  },
  rateButton: {
    paddingLeft: ms(8),
    paddingRight: ms(18),
    height: ms(48),
    alignItems: "center",
    justifyContent: "center",
  },
  rateText: {
    fontSize: ms(12),
    color: Colors.gray9,
    fontWeight: "500",
    letterSpacing: ms(1),
  },
  durationText: {
    fontSize: ms(10),
    color: Colors.subtitle,
    marginRight: ms(12),
  },
  progressContainer: {
    flex: 1,
    marginRight: ms(12),
  },
  progressTrack: {
    height: ms(2),
    backgroundColor: Colors.text + "20",
    borderRadius: ms(2),
    width: "100%",
  },
  transcriptProgressBar: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "100%",
    backgroundColor: Colors.accent,
    borderRadius: ms(2),
  },
  segmentPressable: {
    paddingVertical: ms(4),
    marginVertical: -ms(4),
    paddingHorizontal: ms(8),
    marginHorizontal: -ms(8),
    borderRadius: ms(8),
  },
  selectedSegment: {
    backgroundColor: Colors.accent + "10",
  },
  editButton: {
    width: ms(48),
    height: ms(24),
    alignItems: "center",
    justifyContent: "center",
  },
  editIcon: {
    width: ms(12),
    height: ms(12),
    tintColor: Colors.subtitle,
  },
  highlight: {
    backgroundColor: "rgba(255, 213, 0, 0.4)",
    borderRadius: ms(2),
    padding: ms(2),
  },
});
